import { EntityType } from '@frontend/common';
import { ModuleRendering, SlotRendering } from '@frontend/common-types';
import { Spinner, accountIdFromResolvedEntity } from '@frontend/components/common';
import { InsaneFilter } from '@frontend/components/list';
import { EquipmentStateBadge } from '@frontend/equipment/badge';
import { CommonMessage } from '@frontend/lang';
import { SlotStatus, SlotType } from '@frontend/slot/types';
import { SpotRenderingClient } from '@frontend/spot/api';
import { StockExtended } from '@frontend/stock/types';
import { ShippingNotesDisplay, TransactionStateBadge } from '@frontend/transaction/common';
import { ExtendedTransaction } from '@frontend/transaction/types';
import { FormattedMessage } from 'react-intl';

import { usePlatformModalHost } from '../../../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../../../modal/platform-object-badge/platform-object-badge.component';
import { SpotLayout } from '../../../../spot-rendering';
import OverviewHeader from '../../../header/overview-header';
import { useLayoutRender } from './layout-render.controller';

export const LayoutRender = () => {
    const viewProps = useLayoutRender();
    const { openPlatformDetail } = usePlatformModalHost();

    if (viewProps.isLoading) {
        return <Spinner />;
    }

    if (!viewProps.spot || !viewProps.slots) {
        return (
            <div>
                <FormattedMessage
                    id='spots.layout.spot-not-found'
                    defaultMessage='SPOT not found'
                />
            </div>
        );
    }
    return (
        <div className='bg-background-default flex h-full min-h-0 w-full min-w-0 flex-col gap-6'>
            <OverviewHeader
                showBack
                title={
                    <div className='flex gap-2'>
                        <span>{CommonMessage.OBJECTS.SPOT.DISTRI_SPOTS}</span>
                        <span>/</span>
                        <span>
                            <FormattedMessage
                                id='spots.layout.title'
                                defaultMessage='Layout'
                            />
                        </span>
                    </div>
                }
            />
            <div className='bg-background-surface outline-border-default flex min-h-0 w-full min-w-0 flex-1 flex-col items-center overflow-hidden rounded-xl outline-1 -outline-offset-1'>
                <InsaneFilter
                    searchKeys={['product', 'equipment', 'tag', 'sender', 'receiver']}
                    listId={`spot-layout-${viewProps.spot.id}`}
                    dispatch={viewProps.dispatch}
                />
                <SpotLayout<ModuleRendering, SlotRendering>
                    onSlotClick={(e) =>
                        openPlatformDetail({
                            entityType: EntityType.SLOT,
                            id: e.id,
                            parents: {
                                spot_id: e.spot_id,
                                module_id: e.module_id
                            }
                        })
                    }
                    spot={viewProps.spot}
                    fetchSpotData={(arg) => SpotRenderingClient.fetchSpotRendering(viewProps.currentAccount, viewProps.spot!.id, arg)}
                    fetchModuleData={(accountId, spotId, moduleId) => SpotRenderingClient.fetchSpotModuleRendering(accountId, spotId, moduleId)}
                    enableZoom
                    enableZoomControls
                    className='min-h-0 flex-1'
                    styles={{
                        getSlotColor: (slot) => {
                            if (slot.type === SlotType.TERMINAL) return 'bg-brand-disabled';
                            if (slot.status === SlotStatus.NON_FUNCTIONAL) {
                                return 'bg-brand-disabled';
                            }
                            if (slot.occupied) return 'bg-brand-secondary';
                            return 'bg-brand-muted';
                        }
                    }}
                    slot={{
                        hoverElement: (slot) => {
                            if (slot.type === SlotType.TERMINAL) {
                                return undefined;
                            } else if (slot.stocks.length > 0) {
                                return <SlotStockHoverContent stocks={slot.stocks} />;
                            } else if (slot.transactions.length > 0) {
                                return <SlotTransactionHoverContent transactions={slot.transactions} />;
                            } else {
                                return (
                                    <p className='text-brand-muted justify-start text-lg font-medium'>
                                        <FormattedMessage
                                            id='spots.layout.slot.no-content'
                                            defaultMessage='No content'
                                        />
                                    </p>
                                );
                            }
                        }
                    }}
                />
            </div>
        </div>
    );
};

const SlotStockHoverContent = (props: { stocks: StockExtended[] }) => {
    return (
        <div className='flex max-w-80 flex-col items-start justify-start gap-3 self-stretch'>
            {props.stocks.map((stock) => (
                <div
                    key={stock.id}
                    className='border-border-default flex w-full flex-col items-start justify-start gap-2 border-t pt-3 first:border-t-0 first:pt-0'>
                    {stock.product && (
                        <div className='flex flex-col items-start justify-start gap-0.5 self-stretch'>
                            <div className='text-brand-muted justify-start text-sm font-medium'>{CommonMessage.OBJECTS.PRODUCT.DEFAULT}</div>
                            <PlatformObjectBadge
                                entityType={EntityType.PRODUCT}
                                id={stock.product.entity_id}
                                display={stock.product.name}
                                foreign={stock.product.foreign}
                                accountId={accountIdFromResolvedEntity(stock.product)}
                            />
                        </div>
                    )}
                    {stock.quantity > 1 && (
                        <div className='flex flex-col items-start justify-start gap-0.5 self-stretch'>
                            <div className='text-brand-muted justify-start text-sm font-medium'>{CommonMessage.OBJECTS.COMMON_FIELDS.QUANTITY}</div>
                            <div className='text-foreground-default justify-start text-sm font-medium'>{stock.quantity}</div>
                        </div>
                    )}
                    {stock.equipment && (
                        <div className='flex flex-col items-start justify-start gap-0.5 self-stretch'>
                            <div className='text-brand-muted justify-start text-sm font-medium'>{CommonMessage.OBJECTS.EQUIPMENT.DEFAULT}</div>
                            <PlatformObjectBadge
                                entityType={EntityType.EQUIPMENT}
                                id={stock.equipment.entity_id}
                                display={stock.equipment.name}
                                foreign={stock.equipment.foreign}
                                accountId={accountIdFromResolvedEntity(stock.equipment)}
                            />
                        </div>
                    )}
                    {stock.equipment_state && (
                        <div className='flex flex-col items-start justify-start gap-0.5 self-stretch'>
                            <div className='text-brand-muted justify-start text-sm font-medium'>{CommonMessage.OBJECTS.COMMON_FIELDS.STATUS}</div>
                            <EquipmentStateBadge state={stock.equipment_state} />
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

const SlotTransactionHoverContent = (props: { transactions: ExtendedTransaction[] }) => {
    return (
        <div className='flex max-w-80 flex-col items-start justify-start gap-3 self-stretch'>
            {props.transactions.map((transaction) => (
                <div
                    key={transaction.id}
                    className='border-border-default flex w-full flex-col items-start justify-start gap-2 border-t pt-3 first:border-t-0 first:pt-0'>
                    <div className='flex flex-col items-start justify-start gap-0.5 self-stretch'>
                        <div className='text-brand-muted justify-start text-sm font-medium'>{CommonMessage.OBJECTS.TRANSACTION.DEFAULT}</div>
                        <PlatformObjectBadge
                            entityType={EntityType.TRANSACTION}
                            id={transaction.id}
                            display={transaction.tracking_number ?? undefined}
                        />
                    </div>
                    {transaction.senders && transaction.senders.length > 0 && (
                        <div className='flex flex-col items-start justify-start gap-0.5 self-stretch'>
                            <div className='text-brand-muted justify-start text-sm font-medium'>{CommonMessage.OBJECTS.TRANSACTION.SENDERS}</div>
                            <div className='flex flex-wrap gap-1'>
                                {transaction.senders.map((sender) => (
                                    <PlatformObjectBadge
                                        key={sender.entity_id}
                                        entityType={EntityType.USER}
                                        id={sender.entity_id}
                                        display={sender.name}
                                        foreign={sender.foreign}
                                        accountId={accountIdFromResolvedEntity(sender)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    {transaction.receivers && transaction.receivers.length > 0 && (
                        <div className='flex flex-col items-start justify-start gap-0.5 self-stretch'>
                            <div className='text-brand-muted justify-start text-sm font-medium'>{CommonMessage.OBJECTS.TRANSACTION.RECEIVERS}</div>
                            <div className='flex flex-wrap gap-1'>
                                {transaction.receivers.map((receiver) => (
                                    <PlatformObjectBadge
                                        key={receiver.entity_id}
                                        entityType={EntityType.USER}
                                        id={receiver.entity_id}
                                        display={receiver.name}
                                        foreign={receiver.foreign}
                                        accountId={accountIdFromResolvedEntity(receiver)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    {transaction.state && (
                        <div className='flex flex-col items-start justify-start gap-0.5 self-stretch'>
                            <div className='text-brand-muted justify-start text-sm font-medium'>{CommonMessage.OBJECTS.COMMON_FIELDS.STATUS}</div>
                            <TransactionStateBadge state={transaction.state} />
                        </div>
                    )}
                    {transaction.shipping_notes && (
                        <div className='flex flex-col items-start justify-start gap-0.5 self-stretch'>
                            <div className='text-brand-muted justify-start text-sm font-medium'>{CommonMessage.OBJECTS.TRANSACTION.SHIPPING_NOTES}</div>
                            <ShippingNotesDisplay
                                value={transaction.shipping_notes}
                                className='text-foreground-default justify-start text-sm font-normal'
                            />
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};
